import { baseApi } from "@/services/api";
import type {
  ClientArchivePreflight,
  ClientEligibleArtifact,
  ClientLifecycleView,
  ClientPermanentDeletePreflight,
} from "./clientLifecycle";

export interface MasterAccountRow {
  masterAccountId: string;
  clientId: string;
  /** Human-facing id: YYYY-MM-##### (e.g. 2026-09-87649). */
  customerNumber: string | null;
  businessName: string;
  companyName: string;
  companyAddress: string;
  address_street?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zipCode?: string | null;
  address_country?: string | null;
  billingPhone: string | null;
  companyNumber: string | null;
  billingEmail: string | null;
  kind?: "client" | "music-vendor" | "installer-vendor" | string;
  status: string;
  active: boolean;
  createdAt: string;
  createdOn: string | null;
  pinStatus: "set" | "unset";
  brandCount: number;
  locationCount: number;
  numberOfBrands: number;
  numberOfLocations: number;
  ownerAuth0Sub: string | null;
  /** Active Owner UserMembership.ownerEmail (governed by the Owner Email change flow). */
  ownerEmail: string | null;
  /** Canonical-first composed name (kk-profile → membership snapshot → email fallback). */
  ownerDisplayName: string | null;
  /**
   * Canonical person name projection (additive; list API >= canonical-owner-name).
   * ownerNameSource: "kk-profile" = canonical; "membership" = provisioning
   * snapshot only (no canonical identity yet); "none" = no usable name.
   */
  ownerFirstName?: string | null;
  ownerLastName?: string | null;
  ownerNameSource?: "kk-profile" | "membership" | "none" | string | null;
  /**
   * Canonical Client Brand PORTFOLIO (additive; list API >= brand-portfolio
   * projection): brandIds of the Client's ACTIVE BrandMembership rows — the
   * same relationship as "Brands for {Client}". NOT Location current-operator,
   * NOT MAAL. brandCount === brandIds.length.
   */
  brandIds?: string[];
  /** Soft-delete archive fields (present when view=archived). */
  archived?: boolean;
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletedReason?: string | null;
  previousStatus?: string | null;
  restoredAt?: string | null;
}

export interface CreateMasterAccountRequest {
  businessName: string;
  legalName?: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zipCode: string;
  address_country?: string;
  billingPhone?: string;
  companyNumber?: string;
  billingEmail: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  securityPin?: string;
}

export interface BrandMembershipRow {
  brandId: string;
  name: string;
  slug: string | null;
  industry: string | null;
  status: string | null;
  roleScope: string;
  accessLevel: string;
  grantedAt: string;
  membershipStatus: string;
}

/** Per-user Brand access from GET .../brand-users/{brandId} (runtime Full/Limited). */
export interface BrandUserRow {
  auth0Sub: string;
  email: string | null;
  displayName: string | null;
  clientRole: string | null;
  brandRole: string | null;
  /** Human label from API ("Full" | "Limited"). */
  accessLevel: string;
  roleScope: string;
  status: string;
  assignedLocationCount: number | null;
  grantedAt: string | null;
}

/** Canonical Support Case fields from GET .../support-tickets (toPublicTicket). */
export interface SupportTicketRow {
  ticketId: string;
  masterAccountId?: string;
  businessName?: string | null;
  brandId?: string | null;
  brandName?: string | null;
  summary?: string | null;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Legacy attribute names (retained); prefer closedAt/closedByName/closureNote. */
  resolvedAt?: string | null;
  resolvedByName?: string | null;
  /** Forensic only — do not render in primary Support Case UI. */
  resolvedBySub?: string | null;
  resolutionNote?: string | null;
  /** Owner lifecycle (OPEN → CLOSED, Closed is FINAL) public projection. */
  isClosed?: boolean;
  closedAt?: string | null;
  closedByName?: string | null;
  closureNote?: string | null;
  /**
   * Canonical machine-readable closure actor (Owner decision: a Client may
   * close their own Case). Historical closures project as "staff".
   */
  closedByType?: "staff" | "client" | null;
  closureReason?: "staff_closed" | "client_closed" | null;
  closedByClient?: boolean;
  /** Closed-Case CSAT summary (denormalized). */
  csatRating?: number | null;
  csatSubmittedAt?: string | null;
  csatLowSatisfaction?: boolean;
  eligibleForSupportMode?: boolean;
  hasEmailReplyToken?: boolean;
  reopenedAt?: string | null;
  /** Immutable client-relative sequence (Case: 01). */
  clientCaseNumber?: number | null;
  clientCaseLabel?: string | null;
  priorResolvedAt?: string | null;
  priorResolutionNote?: string | null;
}

/** Client's Closed-Case satisfaction response (one per Case). */
export interface SupportCsatRow {
  ticketId?: string;
  rating: number;
  feedbackText?: string | null;
  lowSatisfaction?: boolean;
  submittedAt?: string | null;
  submittedVia?: string | null;
  brandId?: string | null;
}

/** Canonical SupportMessage from GET .../support-tickets/{id}/messages. */
export interface SupportMessageRow {
  messageId: string;
  ticketId: string;
  masterAccountId?: string;
  direction?: string;
  authorType?: string;
  authorSub?: string | null;
  authorDisplayName?: string | null;
  bodyText?: string;
  bodyHtmlSanitized?: string | null;
  createdAt?: string;
  attachmentDisposition?: string | null;
  /** Inbound email: quoted reply history was trimmed (raw retained server-side). */
  quotedHistoryTrimmed?: boolean;
}

/** Client row from GET /sa/master-accounts/support-attention. */
export interface SupportAttentionClientRow {
  masterAccountId: string;
  businessName?: string | null;
  customerNumber?: string | null;
  openCount: number;
  inProgressCount: number;
  totalNeedingAttention: number;
  /** Historical Support Case count for this masterAccountId (includes resolved). */
  totalCaseCount?: number;
  oldestCreatedAt?: string | null;
  latestUpdatedAt?: string | null;
  brandNames?: string[];
}

export interface SupportAttentionSummary {
  clients: SupportAttentionClientRow[];
  unresolvedCaseCount: number;
  clientsNeedingSupport: number;
  /** Active Clients with ≥1 historical Support Case (full roster size). */
  clientsWithSupportHistory?: number;
  exact: boolean;
  truncated: boolean;
  scannedCaseCount: number;
  maxItems: number | null;
}

/** Live Location Assignment projection for Client Locations page. */
export interface ClientLocationRow {
  locationId: string;
  locationName: string | null;
  brandId: string | null;
  brandName: string | null;
  status?: string | null;
  assignedAt?: string | null;
}

export type {
  ClientArchivePreflight,
  ClientEligibleArtifact,
  ClientLifecycleView,
  ClientPermanentDeletePreflight,
};

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

export const accountsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listMasterAccounts: builder.query<
      { masterAccounts: MasterAccountRow[]; view: ClientLifecycleView },
      { kind?: string; view?: ClientLifecycleView } | void
    >({
      query: (args) => ({
        url: "/sa/master-accounts",
        method: "GET",
        params: { kind: args?.kind || "client", view: args?.view || "active" },
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          masterAccounts: MasterAccountRow[];
          view?: ClientLifecycleView;
        }>,
      ) => {
        const data = unwrap(raw, "Failed to load clients");
        return {
          masterAccounts: data.masterAccounts || [],
          view: data.view === "archived" ? "archived" : "active",
        };
      },
      providesTags: ["Accounts"],
    }),
    createMasterAccount: builder.mutation<
      {
        masterAccount: Partial<MasterAccountRow> & { masterAccountId: string };
        ownerAuth0Sub: string;
        ownerEmail?: string | null;
        ownerFirstName?: string | null;
        ownerLastName?: string | null;
        passwordSetupUrl: string | null;
      },
      CreateMasterAccountRequest
    >({
      query: (body) => ({
        url: "/sa/master-accounts",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to create client"),
      invalidatesTags: ["Accounts"],
    }),
    sendClientPasswordSetupEmail: builder.mutation<
      {
        queued: boolean;
        jobId: string;
        to: string;
        masterAccountId: string;
      },
      { masterAccountId: string; passwordSetupUrl: string }
    >({
      query: ({ masterAccountId, passwordSetupUrl }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/password-setup-email`,
        method: "POST",
        body: { passwordSetupUrl },
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to send password setup email"),
    }),
    getPasswordSetupEmailStatus: builder.query<
      {
        masterAccountId: string;
        status:
          | "not_sent"
          | "submitted"
          | "sent"
          | "delivered"
          | "failed"
          | "bounced";
        deliveryState: string | null;
        lastAttemptAt: string | null;
        deliveredAt: string | null;
        jobId: string | null;
        kind: string | null;
      },
      { masterAccountId: string; jobId?: string | null }
    >({
      query: ({ masterAccountId, jobId }) => {
        const base = `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/password-setup-email/status`;
        const params = new URLSearchParams();
        if (jobId) params.set("jobId", jobId);
        // Bust intermediary GET caches; backend ignores unknown keys.
        params.set("_ts", String(Date.now()));
        return `${base}?${params.toString()}`;
      },
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to load setup email status"),
      // Do not retain an in-flight snapshot after Client Details unmounts.
      keepUnusedDataFor: 15,
    }),
    getOwnerAccount: builder.query<
      {
        masterAccountId: string;
        ownerEmail: string | null;
        ownerFirstName: string | null;
        ownerLastName: string | null;
        ownerPhone: string | null;
        ownerDisplayName: string | null;
        ownerNameSource?: "kk-profile" | "membership" | "none" | string | null;
        accountSetupStatus: "pending" | "complete" | "unknown";
        lastSignInAt: string | null;
        hasOwnerMembership: boolean;
        canGenerateSetupLink: boolean;
      },
      string
    >({
      query: (masterAccountId) =>
        `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/owner-account`,
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to load owner account"),
    }),
    updateOwnerProfile: builder.mutation<
      {
        masterAccountId: string;
        ownerAuth0Sub: string;
        ownerFirstName: string | null;
        ownerLastName: string | null;
        ownerPhone: string | null;
        ownerDisplayName: string | null;
        ownerEmail: string | null;
        ownerNameSource: string;
      },
      {
        masterAccountId: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
      }
    >({
      query: ({ masterAccountId, ...body }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/owner-profile`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to update owner profile"),
    }),
    generateClientPasswordSetupTicket: builder.mutation<
      {
        masterAccountId: string;
        passwordSetupUrl: string;
        expiresInSec: number;
        ownerEmail: string | null;
        accountSetupStatus: "pending" | "complete" | "unknown";
      },
      { masterAccountId: string }
    >({
      query: ({ masterAccountId }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/password-setup-ticket`,
        method: "POST",
        body: {},
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to generate password setup link"),
    }),
    changeClientOwnerEmail: builder.mutation<
      {
        masterAccountId: string;
        ownerEmail: string;
        previousOwnerEmail: string;
        auth0Sub: string;
        billingEmailSynced: boolean;
        model: string;
        unchanged: boolean;
      },
      {
        masterAccountId: string;
        newEmail: string;
        confirmEmail: string;
        confirmed: boolean;
      }
    >({
      query: ({ masterAccountId, newEmail, confirmEmail, confirmed }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/owner-email`,
        method: "POST",
        body: { newEmail, confirmEmail, confirmed },
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to change owner email"),
      invalidatesTags: ["Accounts"],
    }),
    updateMasterAccount: builder.mutation<
      { masterAccount: Partial<MasterAccountRow> },
      {
        masterAccountId: string;
        businessName?: string;
        legalName?: string;
        address_street?: string;
        address_city?: string;
        address_state?: string;
        address_zipCode?: string;
        address_country?: string;
        billingPhone?: string;
        billingEmail?: string;
        active?: boolean;
        status?: string;
      }
    >({
      query: ({ masterAccountId, ...body }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/update`,
        method: "POST",
        body,
      }),
      transformResponse: (
        raw: ApiEnvelope<{ masterAccount: Partial<MasterAccountRow> }>,
      ) => unwrap(raw, "Failed to update client"),
      invalidatesTags: ["Accounts"],
    }),
    createMusicVendor: builder.mutation<
      {
        masterAccount: Partial<MasterAccountRow> & { masterAccountId: string };
        ownerAuth0Sub: string;
        passwordSetupUrl: string | null;
      },
      CreateMasterAccountRequest
    >({
      query: (body) => ({
        url: "/sa/master-accounts/music-vendors",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to create music vendor"),
      invalidatesTags: ["Accounts"],
    }),
    createInstallerVendor: builder.mutation<
      {
        masterAccount: Partial<MasterAccountRow> & { masterAccountId: string };
        ownerAuth0Sub: string;
        passwordSetupUrl: string | null;
      },
      CreateMasterAccountRequest
    >({
      query: (body) => ({
        url: "/sa/master-accounts/installer-vendors",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to create installer vendor"),
      invalidatesTags: ["Accounts"],
    }),
    resetMasterAccountPin: builder.mutation<
      { pinStatus: string },
      { masterAccountId: string; reason: string }
    >({
      query: ({ masterAccountId, reason }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/reset-security-pin`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: (raw: ApiEnvelope<{ pinStatus: string }>) =>
        unwrap(raw, "Failed to reset PIN"),
      invalidatesTags: ["Accounts"],
    }),
    setMasterAccountPin: builder.mutation<
      { pinStatus: string; viewable?: boolean },
      { masterAccountId: string; securityPin: string }
    >({
      query: ({ masterAccountId, securityPin }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/set-security-pin`,
        method: "POST",
        body: { securityPin },
      }),
      transformResponse: (
        raw: ApiEnvelope<{ pinStatus: string; viewable?: boolean }>,
      ) => unwrap(raw, "Failed to set PIN"),
      invalidatesTags: ["Accounts"],
    }),
    getMasterAccountPin: builder.query<
      {
        masterAccountId: string;
        pinStatus: "set" | "unset";
        viewable: boolean;
        securityPin: string | null;
        message?: string;
      },
      string
    >({
      query: (masterAccountId) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/security-pin`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          masterAccountId: string;
          pinStatus: "set" | "unset";
          viewable: boolean;
          securityPin: string | null;
          message?: string;
        }>,
      ) => unwrap(raw, "Failed to load security PIN"),
    }),
    listMasterAccountBrands: builder.query<
      { masterAccountId: string; brands: BrandMembershipRow[] },
      string
    >({
      query: (masterAccountId) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/brands`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{ masterAccountId: string; brands: BrandMembershipRow[] }>,
      ) => unwrap(raw, "Failed to load client brands"),
      providesTags: (_r, _e, id) => [{ type: "Accounts", id }],
    }),
    listBrandUsers: builder.query<
      { masterAccountId: string; brandId: string; users: BrandUserRow[] },
      { masterAccountId: string; brandId: string }
    >({
      query: ({ masterAccountId, brandId }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/brand-users/${encodeURIComponent(brandId)}`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          masterAccountId: string;
          brandId: string;
          users: BrandUserRow[];
        }>,
      ) => unwrap(raw, "Failed to load brand users"),
      providesTags: (_r, _e, arg) => [
        {
          type: "Accounts",
          id: `${arg.masterAccountId}-brand-users-${arg.brandId}`,
        },
      ],
    }),
    listMasterAccountLocations: builder.query<
      {
        masterAccountId: string;
        businessName: string | null;
        locations: ClientLocationRow[];
        locationCount: number;
      },
      string
    >({
      query: (masterAccountId) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/locations`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          masterAccountId: string;
          businessName?: string | null;
          locations: ClientLocationRow[];
          locationCount?: number;
        }>,
      ) => {
        const data = unwrap(raw, "Failed to load client locations");
        const locations = data.locations || [];
        return {
          masterAccountId: data.masterAccountId,
          businessName: data.businessName ?? null,
          locations,
          locationCount:
            typeof data.locationCount === "number"
              ? data.locationCount
              : locations.length,
        };
      },
      providesTags: (_r, _e, id) => [{ type: "Accounts", id }],
    }),
    createMasterAccountLocation: builder.mutation<
      {
        masterAccountId: string;
        locationId: string;
        location: Record<string, unknown>;
        created: boolean;
        idempotentReplay?: boolean;
      },
      {
        masterAccountId: string;
        idempotencyKey: string;
        brandId: string;
        label?: string;
        name?: string;
        storeNumber?: string;
        address: {
          street: string;
          street2?: string;
          city: string;
          state: string;
          zipCode: string;
          country: string;
        };
        timezone: string;
        operatingHours: Record<string, unknown>;
        zones: Array<{
          name: string;
          intendedDeviceClass: string;
          intendedDeviceDescription?: string;
          clientZoneKey?: string;
        }>;
        confirmDuplicateStoreNumber?: boolean;
      }
    >({
      query: ({ masterAccountId, idempotencyKey, ...body }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/locations`,
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: { ...body, idempotencyKey },
      }),
      transformResponse: (raw: ApiEnvelope<Record<string, unknown>>) => {
        const data = unwrap(raw, "Failed to create location") as Record<
          string,
          unknown
        >;
        return {
          masterAccountId: String(data.masterAccountId || ""),
          locationId: String(data.locationId || ""),
          location: (data.location as Record<string, unknown>) || {},
          created: Boolean(data.created),
          idempotentReplay: Boolean(data.idempotentReplay),
        };
      },
      invalidatesTags: (_r, _e, arg) => [
        { type: "Accounts", id: arg.masterAccountId },
        "Accounts",
      ],
    }),
    grantBrandMembership: builder.mutation<
      unknown,
      {
        masterAccountId: string;
        brandId: string;
        roleScope?: string;
        accessLevel?: string;
      }
    >({
      query: ({ masterAccountId, ...body }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/grant-brand-membership`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) =>
        unwrap(raw, "Failed to assign brand"),
      invalidatesTags: ["Accounts"],
    }),
    revokeBrandMembership: builder.mutation<
      unknown,
      { masterAccountId: string; brandId: string; reason?: string }
    >({
      query: ({ masterAccountId, brandId, reason }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/revoke-brand-membership/${encodeURIComponent(brandId)}`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) =>
        unwrap(raw, "Failed to revoke brand access"),
      invalidatesTags: ["Accounts"],
    }),
    /** Legacy compat — requires auth0Sub; prefer updateUserBrandAccess. */
    updateBrandMembership: builder.mutation<
      unknown,
      {
        masterAccountId: string;
        brandId: string;
        auth0Sub: string;
        roleScope: "corporate" | "franchisee";
        reason?: string;
      }
    >({
      query: ({ masterAccountId, brandId, auth0Sub, roleScope, reason }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/update-brand-membership/${encodeURIComponent(brandId)}`,
        method: "POST",
        body: { auth0Sub, roleScope, reason },
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) =>
        unwrap(raw, "Failed to update brand access level"),
      invalidatesTags: (_r, _e, arg) => [
        "Accounts",
        {
          type: "Accounts",
          id: `${arg.masterAccountId}-brand-users-${arg.brandId}`,
        },
      ],
    }),
    updateUserBrandAccess: builder.mutation<
      unknown,
      {
        masterAccountId: string;
        auth0Sub: string;
        brandId: string;
        roleScope: "corporate" | "franchisee";
        reason?: string;
      }
    >({
      query: ({ masterAccountId, ...body }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/update-user-brand-access`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) =>
        unwrap(raw, "Failed to update user brand access"),
      invalidatesTags: (_r, _e, arg) => [
        "Accounts",
        {
          type: "Accounts",
          id: `${arg.masterAccountId}-brand-users-${arg.brandId}`,
        },
      ],
    }),
    startImpersonation: builder.mutation<
      {
        ticketId: string;
        token: string;
        expiresAt: string;
        correlationId: string;
        masterAccountId: string;
        impersonatedUserSub: string;
        userDisplayName: string;
        userEmail?: string | null;
        saDisplayName: string;
        businessName: string;
        supportTicketId: string | null;
        supportModeSource:
          | "existing-ticket"
          | "ad-hoc-created"
          | "sa-maintenance";
        preserveSupportCaseStatus?: boolean;
        maintenanceAccess?: boolean;
        targetPath?: string | null;
        openSupportTicketId?: string | null;
        brandId?: string | null;
        brandName?: string | null;
        supportTicket?: {
          ticketId: string;
          summary?: string | null;
          status?: string;
          brandId?: string | null;
          brandName?: string | null;
          clientCaseNumber?: number | null;
          clientCaseLabel?: string | null;
        } | null;
      },
      {
        masterAccountId: string;
        userKey: string;
        reason: string;
        expectedDurationMinutes?: number;
        supportTicketId?: string;
        createSupportCaseNow?: boolean;
        reservedSupportTicketId?: string;
        brandId?: string;
        /** Impersonate into Case: allow resolved/closed; do not mutate case status. */
        preserveSupportCaseStatus?: boolean;
        impersonateIntoCase?: boolean;
        /** Super Admin only: no Support Case created, linked, or notified. */
        maintenanceAccess?: boolean;
      }
    >({
      query: ({ masterAccountId, userKey, ...body }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/users/${encodeURIComponent(userKey)}/impersonate`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to start impersonation"),
    }),
    validateSupportTicket: builder.mutation<
      {
        valid: boolean;
        supportTicket: {
          ticketId: string;
          summary?: string | null;
          status?: string;
          businessName?: string | null;
          brandId?: string | null;
          brandName?: string | null;
          eligibleForSupportMode?: boolean;
        };
      },
      { masterAccountId: string; ticketId: string }
    >({
      query: ({ masterAccountId, ticketId }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/support-tickets/validate`,
        method: "POST",
        body: { ticketId },
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to validate support ticket"),
    }),
    createAdhocSupportTicket: builder.mutation<
      {
        created: boolean;
        supportTicket: {
          ticketId: string;
          summary?: string | null;
          status?: string;
          businessName?: string | null;
          eligibleForSupportMode?: boolean;
        };
      },
      {
        masterAccountId: string;
        summary: string;
        expectedDurationMinutes?: number;
      }
    >({
      query: ({ masterAccountId, summary, expectedDurationMinutes }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/support-tickets`,
        method: "POST",
        body: { summary, expectedDurationMinutes },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Accounts", id: `support-tickets-${arg.masterAccountId}` },
        { type: "Accounts", id: "support-attention" },
      ],
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to create support case"),
    }),
    reserveSupportTicket: builder.mutation<
      {
        reserved: boolean;
        supportTicket: {
          ticketId: string;
          status?: string;
          reservedExpiresAt?: string | null;
        };
      },
      { masterAccountId: string }
    >({
      query: ({ masterAccountId }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/support-tickets/reserve`,
        method: "POST",
        body: {},
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to reserve Case ID"),
    }),
    listSupportTickets: builder.query<
      { masterAccountId: string; supportTickets: SupportTicketRow[] },
      { masterAccountId: string }
    >({
      query: ({ masterAccountId }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/support-tickets`,
        method: "GET",
      }),
      providesTags: (_r, _e, arg) => [
        { type: "Accounts", id: `support-tickets-${arg.masterAccountId}` },
      ],
      transformResponse: (raw: ApiEnvelope<any>, _meta, arg) => {
        const data = unwrap(raw, "Failed to load support tickets") as {
          masterAccountId?: string;
          supportTickets?: SupportTicketRow[];
        };
        return {
          masterAccountId: data.masterAccountId || arg.masterAccountId,
          supportTickets: Array.isArray(data.supportTickets)
            ? data.supportTickets
            : [],
        };
      },
    }),
    /**
     * Close a Support Case (Owner lifecycle: OPEN → CLOSED, Closed is FINAL).
     * POST …/support-tickets/{id}/close — no reopen exists.
     */
    closeSupportTicket: builder.mutation<
      {
        closed: boolean;
        /** legacy field kept by the API for one release */
        resolved?: boolean;
        closeVia?: string;
        supportTicket: SupportTicketRow;
        customerEmail?: { enqueued?: boolean; reason?: string } | null;
      },
      {
        masterAccountId: string;
        ticketId: string;
        closureNote?: string;
      }
    >({
      query: ({ masterAccountId, ticketId, closureNote }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/support-tickets/${encodeURIComponent(ticketId)}/close`,
        method: "POST",
        body: closureNote ? { closureNote } : {},
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Accounts", id: `support-tickets-${arg.masterAccountId}` },
        {
          type: "Accounts",
          id: `support-messages-${arg.masterAccountId}-${arg.ticketId}`,
        },
        { type: "Accounts", id: "support-attention" },
      ],
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to close support case"),
    }),
    listSupportMessages: builder.query<
      {
        masterAccountId: string;
        supportTicket: SupportTicketRow;
        messages: SupportMessageRow[];
        /** Client's Closed-Case satisfaction response (null until submitted / open Case). */
        csat: SupportCsatRow | null;
      },
      { masterAccountId: string; ticketId: string }
    >({
      query: ({ masterAccountId, ticketId }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/support-tickets/${encodeURIComponent(ticketId)}/messages`,
        method: "GET",
      }),
      providesTags: (_r, _e, arg) => [
        {
          type: "Accounts",
          id: `support-messages-${arg.masterAccountId}-${arg.ticketId}`,
        },
      ],
      transformResponse: (raw: ApiEnvelope<any>, _meta, arg) => {
        const data = unwrap(raw, "Failed to load support messages") as {
          masterAccountId?: string;
          supportTicket?: SupportTicketRow;
          messages?: SupportMessageRow[];
          csat?: SupportCsatRow | null;
        };
        return {
          masterAccountId: data.masterAccountId || arg.masterAccountId,
          supportTicket: data.supportTicket || { ticketId: arg.ticketId },
          messages: Array.isArray(data.messages) ? data.messages : [],
          csat: data.csat || null,
        };
      },
    }),
    postSupportMessage: builder.mutation<
      {
        message: SupportMessageRow;
        supportTicket?: SupportTicketRow;
        email?: { enqueued?: boolean; jobId?: string } | null;
      },
      { masterAccountId: string; ticketId: string; bodyText: string }
    >({
      query: ({ masterAccountId, ticketId, bodyText }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/support-tickets/${encodeURIComponent(ticketId)}/messages`,
        method: "POST",
        body: { bodyText },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Accounts", id: `support-tickets-${arg.masterAccountId}` },
        {
          type: "Accounts",
          id: `support-messages-${arg.masterAccountId}-${arg.ticketId}`,
        },
        { type: "Accounts", id: "support-attention" },
      ],
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to post support message"),
    }),
    getSupportAttention: builder.query<
      SupportAttentionSummary,
      { maxItems?: number; clientLimit?: number } | void
    >({
      query: (args) => ({
        url: `/sa/master-accounts/support-attention`,
        method: "GET",
        params: {
          maxItems: args?.maxItems ?? 500,
          ...(args?.clientLimit != null
            ? { clientLimit: args.clientLimit }
            : {}),
        },
      }),
      providesTags: [{ type: "Accounts", id: "support-attention" }],
      transformResponse: (raw: ApiEnvelope<any>) => {
        const data = unwrap(raw, "Failed to load support attention") as Partial<
          SupportAttentionSummary
        >;
        return {
          clients: Array.isArray(data.clients) ? data.clients : [],
          unresolvedCaseCount: Number(data.unresolvedCaseCount) || 0,
          clientsNeedingSupport: Number(data.clientsNeedingSupport) || 0,
          exact: data.exact !== false,
          truncated: Boolean(data.truncated),
          scannedCaseCount: Number(data.scannedCaseCount) || 0,
          maxItems: Number(data.maxItems) || 500,
        };
      },
    }),
    archiveMasterAccount: builder.mutation<
      | { preflight: ClientArchivePreflight }
      | {
          masterAccount: Partial<MasterAccountRow>;
          alreadyArchived?: boolean;
          revokedArtifactIds?: string[];
          impact?: Record<string, unknown> | null;
          correlationId?: string | null;
        },
      {
        masterAccountId: string;
        nameConfirmation?: string;
        reason?: string;
        dryRun?: boolean;
      }
    >({
      query: ({ masterAccountId, nameConfirmation, reason, dryRun }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/archive`,
        method: "POST",
        body: {
          ...(nameConfirmation != null ? { nameConfirmation } : {}),
          ...(reason != null ? { reason } : {}),
          ...(dryRun === true ? { dryRun: true } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<
          | { preflight: ClientArchivePreflight }
          | {
              masterAccount: Partial<MasterAccountRow>;
              alreadyArchived?: boolean;
              revokedArtifactIds?: string[];
              impact?: Record<string, unknown> | null;
              correlationId?: string | null;
            }
        >,
      ) => unwrap(raw, "Failed to archive client"),
      invalidatesTags: (_result, _error, arg) =>
        arg.dryRun === true ? [] : ["Accounts"],
    }),
    getClientRestoreEligibility: builder.query<
      {
        masterAccount: Partial<MasterAccountRow>;
        eligibleArtifacts: ClientEligibleArtifact[];
      },
      string
    >({
      query: (masterAccountId) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/restore`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          masterAccount: Partial<MasterAccountRow>;
          eligibleArtifacts: ClientEligibleArtifact[];
        }>,
      ) => {
        const data = unwrap(raw, "Failed to load eligible restore artifacts");
        return {
          masterAccount: data.masterAccount,
          eligibleArtifacts: data.eligibleArtifacts || [],
        };
      },
    }),
    restoreMasterAccount: builder.mutation<
      {
        masterAccount: Partial<MasterAccountRow>;
        restoredArtifactIds?: string[];
        eligibleArtifactIds?: string[];
        correlationId?: string | null;
      },
      { masterAccountId: string; artifactIds: string[]; notes?: string }
    >({
      query: ({ masterAccountId, artifactIds, notes }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/restore`,
        method: "POST",
        body: {
          artifactIds,
          ...(notes != null && notes !== "" ? { notes } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          masterAccount: Partial<MasterAccountRow>;
          restoredArtifactIds?: string[];
          eligibleArtifactIds?: string[];
          correlationId?: string | null;
        }>,
      ) => unwrap(raw, "Failed to restore client"),
      invalidatesTags: ["Accounts"],
    }),
    permanentDeleteMasterAccount: builder.mutation<
      | { preflight: ClientPermanentDeletePreflight; alreadyDeleted?: boolean }
      | {
          masterAccountId: string;
          alreadyDeleted?: boolean;
          businessName?: string | null;
          cleanup?: {
            channelPreferencesDeleted?: number;
            releasedEmails?: string[];
          } | null;
          hardRevokedArtifactIds?: string[];
          correlationId?: string | null;
        },
      {
        masterAccountId: string;
        nameConfirmation: string;
        confirm: "DELETE";
        reason: string;
        dryRun?: boolean;
      }
    >({
      query: ({
        masterAccountId,
        nameConfirmation,
        confirm,
        reason,
        dryRun,
      }) => ({
        url: `/sa/master-accounts/${encodeURIComponent(masterAccountId)}/permanent-delete`,
        method: "POST",
        body: {
          nameConfirmation,
          confirm,
          reason,
          ...(dryRun === true ? { dryRun: true } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<
          | {
              preflight: ClientPermanentDeletePreflight;
              alreadyDeleted?: boolean;
            }
          | {
              masterAccountId: string;
              alreadyDeleted?: boolean;
              businessName?: string | null;
              cleanup?: {
                channelPreferencesDeleted?: number;
                releasedEmails?: string[];
              } | null;
              hardRevokedArtifactIds?: string[];
              correlationId?: string | null;
            }
        >,
      ) => unwrap(raw, "Failed to permanently delete client"),
      invalidatesTags: (_result, _error, arg) =>
        arg.dryRun === true ? [] : ["Accounts"],
    }),
    endImpersonation: builder.mutation<unknown, { ticketId: string }>({
      query: ({ ticketId }) => ({
        url: `/sa/impersonation-tickets/${encodeURIComponent(ticketId)}/end`,
        method: "POST",
        body: {},
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) =>
        unwrap(raw, "Failed to end impersonation"),
    }),
  }),
});

export const {
  useListMasterAccountsQuery,
  useCreateMasterAccountMutation,
  useSendClientPasswordSetupEmailMutation,
  useGetPasswordSetupEmailStatusQuery,
  useLazyGetPasswordSetupEmailStatusQuery,
  useGetOwnerAccountQuery,
  useLazyGetOwnerAccountQuery,
  useUpdateOwnerProfileMutation,
  useGenerateClientPasswordSetupTicketMutation,
  useChangeClientOwnerEmailMutation,
  useUpdateMasterAccountMutation,
  useCreateMusicVendorMutation,
  useCreateInstallerVendorMutation,
  useResetMasterAccountPinMutation,
  useSetMasterAccountPinMutation,
  useLazyGetMasterAccountPinQuery,
  useListMasterAccountBrandsQuery,
  useListMasterAccountLocationsQuery,
  useCreateMasterAccountLocationMutation,
  useGrantBrandMembershipMutation,
  useRevokeBrandMembershipMutation,
  useUpdateBrandMembershipMutation,
  useListBrandUsersQuery,
  useUpdateUserBrandAccessMutation,
  useStartImpersonationMutation,
  useValidateSupportTicketMutation,
  useCreateAdhocSupportTicketMutation,
  useReserveSupportTicketMutation,
  useListSupportTicketsQuery,
  useLazyListSupportTicketsQuery,
  useCloseSupportTicketMutation,
  useListSupportMessagesQuery,
  usePostSupportMessageMutation,
  useGetSupportAttentionQuery,
  useLazyGetSupportAttentionQuery,
  useEndImpersonationMutation,
  useArchiveMasterAccountMutation,
  useGetClientRestoreEligibilityQuery,
  useLazyGetClientRestoreEligibilityQuery,
  useRestoreMasterAccountMutation,
  usePermanentDeleteMasterAccountMutation,
} = accountsApi;
